/*
  <cart-status> — the cart drawer's promotional messaging, in one place.

  Replaces two pasted-in jQuery blocks that each ran setInterval(…, 1200)
  forever: one polling /cart.js to write "unlock your discount" text, one
  re-scraping the rendered per-line discount pills to sum a savings total.
  Both are now event-driven and read the cart JSON directly, which also makes
  the savings figure currency-safe instead of regex-matching an "R" prefix.
*/
(function () {
  'use strict';

  if (window.customElements && customElements.get('cart-status')) return;

  var CART_EVENTS = ['theme:cart:change', 'theme:cart:reload'];
  var DEBOUNCE_MS = 250;

  /* Standard Shopify money formatter, driven by window.theme.moneyFormat. */
  function formatMoney(cents, format) {
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';

      var parts = (number / 100.0).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var match = format.match(placeholder);
    var value;

    switch (match && match[1]) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ','); break;
      default: value = withDelimiters(cents, 2);
    }

    return format.replace(placeholder, value);
  }

  var CartStatus = class CartStatus extends HTMLElement {
    constructor() {
      super();
      this.timer = null;
      this.onCartEvent = this.onCartEvent.bind(this);
    }

    connectedCallback() {
      this.primary = this.querySelector('[data-cart-status-message]');
      this.secondary = this.querySelector('[data-cart-status-secondary]');
      if (!this.primary) return;

      var root = this.closest('.cart__drawer') || document;
      this.savings = root.querySelector('[data-cart-savings]');
      /* The theme's own free shipping message, complete with progress wheel. */
      this.shippingMessage = root.querySelector('[data-cart-message]');

      this.limit = parseFloat(this.dataset.freeShippingLimit) || 0;
      this.minItems = parseInt(this.dataset.discountMinItems, 10) || 0;

      CART_EVENTS.forEach(function (name) {
        document.addEventListener(name, this.onCartEvent);
      }, this);

      this.refresh();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      CART_EVENTS.forEach(function (name) {
        document.removeEventListener(name, this.onCartEvent);
      }, this);
    }

    onCartEvent() {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.refresh.bind(this), DEBOUNCE_MS);
    }

    refresh() {
      var self = this;

      return fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin'
      })
        .then(function (response) { return response.json(); })
        .then(function (cart) { self.apply(cart); })
        .catch(function (error) { console.warn('[cart-status] refresh failed', error); });
    }

    apply(cart) {
      if (!cart || !cart.item_count) {
        this.setLine(this.primary, '');
        this.setLine(this.secondary, '');
        this.setLine(this.savings, '');
        this.suppressShipping(true);
        return;
      }

      var hasDiscount = (cart.total_discount || 0) > 0;
      var hasShipping = this.shippingQualified(cart);
      var nudge = this.nudgeText(cart, hasDiscount);

      if (hasDiscount && hasShipping) {
        this.setLine(this.primary, this.dataset.textBoth);
        this.setLine(this.secondary, '');
      } else if (hasDiscount) {
        this.setLine(this.primary, this.dataset.textDiscount);
        this.setLine(this.secondary, '');
      } else if (hasShipping) {
        this.setLine(this.primary, this.dataset.textShipping);
        this.setLine(this.secondary, nudge);
      } else {
        /*
          Nothing earned yet: lead with the discount alone. The free shipping
          goal is already on the upsell cards as a per-product badge, and two
          simultaneous "spend more" asks cancel each other out.
        */
        this.setLine(this.primary, nudge);
        this.setLine(this.secondary, '');
      }

      /*
        The theme's shipping message earns its place in exactly one state: she
        has the discount but not free delivery, so its wheel shows the one goal
        still open. Everywhere else this element has already said it, or is
        deliberately staying quiet.
      */
      this.suppressShipping(!(hasDiscount && !hasShipping));

      this.renderSavings(cart, hasDiscount);
    }

    shippingQualified(cart) {
      if (!this.limit) return false;
      return (cart.total_price || 0) >= this.limit * 100 * this.currencyRate();
    }

    currencyRate() {
      var rate = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
      return rate > 0 ? rate : 1;
    }

    nudgeText(cart, hasDiscount) {
      if (hasDiscount || !this.minItems) return '';

      var remaining = this.minItems - cart.item_count;
      /*
        Guard the case where she has enough items but the discount still didn't
        apply (excluded products, expired promo) — "Add 0 more" is worse than
        saying nothing.
      */
      if (remaining < 1) return '';

      return remaining === 1
        ? this.dataset.textNudgeSingle
        : (this.dataset.textNudgePlural || '').replace('||count||', remaining);
    }

    renderSavings(cart, hasDiscount) {
      if (!this.savings) return;

      if (!hasDiscount) {
        this.setLine(this.savings, '');
        return;
      }

      var format = (window.theme && window.theme.moneyFormat) || '{{amount}}';
      var amount = formatMoney(cart.total_discount, format);
      var title = this.discountTitle(cart);

      var text = (this.savings.dataset.textSavings || '')
        .replace('||amount||', amount)
        .replace('||title||', title);

      /* Without a title the sentence reads "…with " — trim back to the amount. */
      if (!title) text = text.replace(/\s*with\s*$/i, '');

      this.setLine(this.savings, text);
    }

    discountTitle(cart) {
      var cartLevel = cart.cart_level_discount_applications || [];
      if (cartLevel.length && cartLevel[0].title) return cartLevel[0].title;

      /*
        Line-level allocations carry the unescaped title; the sibling discounts[]
        array HTML-escapes it, which would render as "you&#39;ve" in textContent.
      */
      var items = cart.items || [];
      for (var i = 0; i < items.length; i++) {
        var allocations = items[i].line_level_discount_allocations || [];
        if (allocations.length && allocations[0].discount_application) {
          return allocations[0].discount_application.title || '';
        }
      }

      return '';
    }

    setLine(el, text) {
      if (!el) return;

      if (text) {
        el.textContent = text;
        el.removeAttribute('hidden');
      } else {
        el.textContent = '';
        el.setAttribute('hidden', '');
      }
    }

    suppressShipping(suppressed) {
      if (!this.shippingMessage) return;
      this.shippingMessage.classList.toggle('cart__message--suppressed', suppressed);
    }
  };

  customElements.define('cart-status', CartStatus);
})();
